import Form         from "../../components/Form/Form";
import EmailInput   from "../../components/Form/FormElements/EmailInput"
import TextInput    from "../../components/Form/FormElements/TextInput";
import SubmitButton from "../../components/Form/FormElements/SubmitButton";
import SEO from "../../components/SEO";

import { useNavigate }         from "react-router-dom";
import { useState, useEffect } from "react";

import axios       from "axios"; 
import Cookies     from "js-cookie";

import "./login.css";

import useUser from "../../hooks/User";
import config        from "../../Config";
import { ROUTES } from "../../routes";
import { safeStartViewTransition } from "../../utils/viewTransition";
import { parsePhoneInputToNationalDigits, formatNationalAsE164Display } from "../../utils/russianPhone";

// Error message component
function ErrorMessage({ message, onDismiss }) {
    return (
        <div className="auth-error" role="alert">
            <div className="auth-error-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            </div>
            <div className="auth-error-content">
                <p className="auth-error-text">{message}</p>
            </div>
            {onDismiss && (
                <button className="auth-error-close" onClick={onDismiss} aria-label="Закрыть">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18"></path>
                        <path d="m6 6 12 12"></path>
                    </svg>
                </button>
            )}
        </div>
    );
}

// Success/info message component
function InfoMessage({ message }) {
    return (
        <div className="auth-info" role="status">
            <div className="auth-info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            </div>
            <p className="auth-info-text">{message}</p>
        </div>
    );
}

function LoginEmail() {
    const { error, loading }    = useUser();
    const nav                   = useNavigate();

    // Auth method: 'email' | 'phone'
    const [authMethod, setAuthMethod] = useState("email");

    // Step: 'email' | 'code'
    const [step, setStep]       = useState("email");
    
    // Email step
    const [email, setEmail]     = useState("");
    const [isSendingCode, setIsSendingCode] = useState(false);
    
    // Code step
    const [code, setCode]       = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    
    // Phone step
    const [phoneNational, setPhoneNational] = useState("");
    const [isSendingSms, setIsSendingSms] = useState(false);
    
    // Resend timer
    const [resendTimer, setResendTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    
    // Common
    const [errorMessage, setErrorMessage] = useState("");
    const [isOAuthLoading, setIsOAuthLoading] = useState({ yandex: false, mail: false, telegram: false, vk: false });
    const [isMaxWaiting, setIsMaxWaiting] = useState(false);
    const [maxSessionId, setMaxSessionId] = useState(null);

    // Redirect if already logged in
    useEffect(() => {
        if (!error && !loading) {
            nav(ROUTES.PROFILE, { viewTransition: true });
        }
    }, [error, loading, nav]);

    // Timer for resend code
    useEffect(() => {
        let interval;
        if (step === "code" && resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, resendTimer]);

    // Send verification code
    const handleSendCode = () => {
        if (!email) return;
        
        setIsSendingCode(true);
        setErrorMessage("");

        axios.get("/general/send_register_or_authenticate_verification_code_on_email/", {
            params: { email: email }
        })
        .then((response) => {
            if (200 <= response.status && response.status < 300) {
                setStep("code");
                setResendTimer(60);
                setCanResend(false);
            }
        })
        .catch((reason) => {
            const errorDesc = reason?.response?.data?.error_description;
            
            if (errorDesc?.includes("Много попыток") || errorDesc?.includes("повторите позже")) {
                setErrorMessage("Слишком много попыток. Пожалуйста, подождите немного перед следующей попыткой.");
            } else if (errorDesc) {
                setErrorMessage(errorDesc);
            } else if (reason?.response?.status === 429) {
                setErrorMessage("Слишком много запросов. Пожалуйста, попробуйте позже.");
            } else {
                setErrorMessage("Не удалось отправить код. Пожалуйста, попробуйте позже.");
            }
        })
        .finally(() => {
            setIsSendingCode(false);
        });
    };

    // Verify code and authenticate
    const handleVerifyCode = () => {
        if (!code || code.length < 6) {
            setErrorMessage("Введите код подтверждения");
            return;
        }

        setIsVerifying(true);
        setErrorMessage("");

        axios.get("/general/register_or_authenticate", {
            params: { verification_code_value: code }
        })
        .then((response) => {
            if (200 <= response.status && response.status < 300) {
                const userValue = response.data.value;
                Cookies.set(config.authCookie, userValue, {
                    expires: 30 * 6
                });
                
                // Notify UserSessionContext to refetch user data
                window.dispatchEvent(new CustomEvent('user:login'));
                
                safeStartViewTransition(() => {
                    nav(ROUTES.PROFILE, { viewTransition: true });
                });
            }
        })
        .catch((reason) => {
            const errorDesc = reason?.response?.data?.error_description;
            
            if (errorDesc) {
                setErrorMessage(errorDesc);
            } else {
                setErrorMessage("Неверный код подтверждения. Попробуйте ещё раз.");
            }
        })
        .finally(() => {
            setIsVerifying(false);
        });
    };

    // Go back to email step
    const handleBackToEmail = () => {
        setStep("email");
        setCode("");
        setErrorMessage("");
        setResendTimer(60);
        setCanResend(false);
    };

    const handleSendSmsCode = () => {
        if (phoneNational.length !== 10) {
            setErrorMessage("Введите номер полностью");
            return;
        }

        const normalized = "+7" + phoneNational;

        setIsSendingSms(true);
        setErrorMessage("");

        axios.get("/general/request_sms_authentication", {
            params: { phone_number: normalized }
        })
        .then((response) => {
            if (200 <= response.status && response.status < 300) {
                const { auth_code, target_sms_number, message, sms_text } = response.data;
                nav(ROUTES.AUTH_SMS_VERIFY, {
                    state: {
                        phoneNumber: normalized,
                        authCode: auth_code,
                        targetSmsNumber: target_sms_number,
                        message,
                        smsText: sms_text,
                        flow: "login"
                    },
                    viewTransition: true
                });
            }
        })
        .catch((reason) => {
            const errorDesc = reason?.response?.data?.error_description;
            
            if (errorDesc?.includes("Много попыток") || errorDesc?.includes("повторите позже") || errorDesc?.includes("rate-limit")) {
                setErrorMessage("Слишком много попыток. Пожалуйста, подождите немного перед следующей попыткой.");
            } else if (errorDesc) {
                setErrorMessage(errorDesc);
            } else if (reason?.response?.status === 429) {
                setErrorMessage("Слишком много запросов. Пожалуйста, попробуйте позже.");
            } else {
                setErrorMessage("Не удалось отправить запрос. Пожалуйста, попробуйте позже.");
            }
        })
        .finally(() => {
            setIsSendingSms(false);
        });
    };

    // Resend verification code
    const handleResendCode = () => {
        if (!canResend || !email) return;
        
        setIsSendingCode(true);
        setErrorMessage("");

        axios.get("/general/send_register_or_authenticate_verification_code_on_email/", {
            params: { email: email }
        })
        .then((response) => {
            if (200 <= response.status && response.status < 300) {
                setResendTimer(60);
                setCanResend(false);
                // Show success message
                setErrorMessage("");
            }
        })
        .catch((reason) => {
            const errorDesc = reason?.response?.data?.error_description;
            
            if (errorDesc?.includes("Много попыток") || errorDesc?.includes("повторите позже")) {
                setErrorMessage("Слишком много попыток. Пожалуйста, подождите немного перед следующей попыткой.");
            } else if (errorDesc) {
                setErrorMessage(errorDesc);
            } else if (reason?.response?.status === 429) {
                setErrorMessage("Слишком много запросов. Пожалуйста, попробуйте позже.");
            } else {
                setErrorMessage("Не удалось отправить код. Пожалуйста, попробуйте позже.");
            }
        })
        .finally(() => {
            setIsSendingCode(false);
        });
    };

    // Handle OAuth authorization with error handling
    const handleOAuthAuthorize = async (provider) => {
        setIsOAuthLoading(prev => ({ ...prev, [provider]: true }));
        setErrorMessage("");

        try {
            const redirectUri = encodeURIComponent(`${window.location.origin}${ROUTES.OAUTH_CALLBACK}`);
            const apiUrl = `${config.getApiBaseUrl()}/general/oauth/${provider}/authorize?api_key=${config.apiKey}&redirect_uri=${redirectUri}`;
            
            // Сначала проверяем ответ через fetch с redirect: 'manual'
            // Это позволит нам обработать ошибки перед редиректом
            const response = await fetch(apiUrl, {
                method: 'GET',
                redirect: 'manual',
                cache: 'no-store',
            });

            // Если это ошибка (400, 401, 403, 500, 503 и т.д.) - парсим JSON и показываем ошибку
            if (response.status >= 400) {
                let errorDesc = "Ошибка авторизации";
                try {
                    const errorData = await response.json();
                    errorDesc = errorData?.error_description 
                        || errorData?.error_description_data?.error_message 
                        || errorData?.error_description_data?.error_message
                        || "Ошибка авторизации";
                } catch (parseErr) {
                    // Если не удалось распарсить JSON, используем статус
                    errorDesc = `Ошибка сервера (${response.status})`;
                }
                setErrorMessage(errorDesc);
                setIsOAuthLoading(prev => ({ ...prev, [provider]: false }));
                return;
            }

            // Если это редирект (302, 301, 307, 308) - переходим по нему
            if (response.status === 302 || response.status === 301 || response.status === 307 || response.status === 308) {
                const redirectUrl = response.headers.get('Location');
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }
                // Если Location недоступен (CORS), просто переходим по исходному URL
                // Браузер сам обработает редирект
                window.location.href = apiUrl;
                return;
            }

            // Если статус OK (200) - это не должно происходить для authorize, но на всякий случай
            // Если статус не редирект и не ошибка - пытаемся перейти по URL
            window.location.href = apiUrl;
        } catch (err) {
            // Если ошибка сети или парсинга
            console.error('OAuth authorization error:', err);
            setErrorMessage("Не удалось подключиться к серверу. Пожалуйста, попробуйте позже.");
            setIsOAuthLoading(prev => ({ ...prev, [provider]: false }));
        }
    };

    const oauthBusy =
        isOAuthLoading.yandex ||
        isOAuthLoading.mail ||
        isOAuthLoading.telegram ||
        isOAuthLoading.vk ||
        isMaxWaiting;

    // Вход через бота MAX: диплинк + поллинг статуса (как SMS, не OAuth-редирект)
    const handleMaxLogin = async () => {
        setErrorMessage("");
        setIsMaxWaiting(true);
        // Открываем вкладку СИНХРОННО, пока браузер считает это реакцией на клик:
        // window.open после await блокируется как popup (Chrome на десктопе).
        const popup = window.open("", "_blank", "noopener,noreferrer");
        try {
            const response = await axios.post("/general/auth/max/init");
            const sessionId = response.data?.session_id;
            const deepLink = response.data?.deep_link;
            if (!sessionId || !deepLink) {
                if (popup) popup.close();
                setIsMaxWaiting(false);
                setErrorMessage("Не удалось начать вход через MAX.");
                return;
            }
            if (popup) {
                popup.location = deepLink;
            } else {
                // Popup-блокировщик сработал — вкладку не удалось открыть
                setIsMaxWaiting(false);
                setErrorMessage(
                    "Браузер заблокировал открытие MAX. Разрешите всплывающие окна для этого сайта и попробуйте снова."
                );
                return;
            }
            setMaxSessionId(sessionId);
        } catch (reason) {
            if (popup) popup.close();
            setIsMaxWaiting(false);
            const errorDesc = reason?.response?.data?.error_description;
            setErrorMessage(errorDesc || "Вход через MAX недоступен. Попробуйте позже.");
        }
    };

    useEffect(() => {
        if (!maxSessionId) {
            return undefined;
        }
        let cancelled = false;
        const startedAt = Date.now();
        const maxWaitMs = 5 * 60 * 1000;

        const stopWaiting = () => {
            setIsMaxWaiting(false);
            setMaxSessionId(null);
        };

        const poll = async () => {
            if (Date.now() - startedAt > maxWaitMs) {
                if (!cancelled) {
                    setErrorMessage("Время ожидания MAX истекло. Попробуйте ещё раз.");
                    stopWaiting();
                }
                return true;
            }
            try {
                const response = await axios.get("/general/auth/max/status", {
                    params: { session_id: maxSessionId },
                });
                if (cancelled) {
                    return false;
                }
                const status = response.data?.status;
                if (status === "authenticated" && response.data?.user_token) {
                    Cookies.set(config.authCookie, response.data.user_token, {
                        expires: 30 * 6,
                    });
                    window.dispatchEvent(new CustomEvent("user:login"));
                    stopWaiting();
                    safeStartViewTransition(() => {
                        nav(ROUTES.PROFILE, { viewTransition: true });
                    });
                    return true;
                }
                if (status === "expired") {
                    setErrorMessage("Сессия MAX истекла. Попробуйте ещё раз.");
                    stopWaiting();
                    return true;
                }
            } catch (reason) {
                const httpStatus = reason?.response?.status;
                if (!cancelled && httpStatus && httpStatus >= 400 && httpStatus < 500 && httpStatus !== 429) {
                    const errorDesc = reason?.response?.data?.error_description;
                    setErrorMessage(errorDesc || "Не удалось проверить статус входа MAX.");
                    stopWaiting();
                    return true;
                }
            }
            return false;
        };

        let intervalId;
        (async () => {
            const done = await poll();
            if (done || cancelled) {
                return;
            }
            intervalId = setInterval(async () => {
                const finished = await poll();
                if (finished && intervalId) {
                    clearInterval(intervalId);
                }
            }, 2000);
        })();

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [maxSessionId, nav]);

    return (
        <>
        <SEO 
            title="Вход в систему"
            description="Войдите в свою учетную запись Spiria для доступа к медицинским опросникам и мониторингу здоровья."
            keywords="вход, авторизация, логин, spiria"
            noindex={true}
        />
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <div className="auth-logo">
                        <svg 
                            version="1.0" 
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 294 295"
                            preserveAspectRatio="xMidYMid slice"
                            fill="currentColor"
                        >
                            <g transform="translate(0.000000,295.000000) scale(0.100000,-0.100000)"
                               fill="#2f6fed"
                               stroke="none">
                                <path d="M1281 2855 c-215 -47 -403 -162 -535 -327 -294 -367 -266 -893 63
-1227 166 -168 402 -271 620 -271 l65 0 -39 -41 c-213 -220 -572 -258 -836
-87 -191 122 -309 340 -309 568 0 41 5 95 11 120 8 35 6 63 -7 130 -11 54 -18
135 -18 222 -1 131 -2 136 -19 120 -31 -27 -105 -145 -137 -217 -60 -133 -75
-213 -74 -385 1 -138 4 -165 27 -243 48 -155 117 -276 221 -385 333 -349 866
-385 1246 -85 196 155 314 377 344 648 l11 100 58 -70 c202 -244 221 -557 50
-817 -62 -94 -150 -171 -258 -224 -138 -68 -282 -90 -418 -63 -46 10 -70 8
-144 -7 -58 -12 -134 -18 -220 -18 -73 -1 -133 -2 -133 -3 0 -11 146 -106 215
-139 145 -71 223 -87 405 -87 140 0 164 2 243 26 167 51 303 134 418 253 110
116 177 232 225 389 25 82 27 104 27 245 0 182 -16 260 -87 405 -95 198 -248
347 -450 441 -92 42 -176 63 -306 78 l-105 12 70 57 c145 120 301 173 480 164
178 -10 304 -66 436 -197 149 -147 208 -302 195 -514 -4 -61 -8 -122 -8 -136
-1 -14 7 -71 16 -128 12 -67 17 -138 14 -206 -4 -122 -4 -122 77 -2 65 96 117
221 141 336 24 122 16 314 -20 433 -100 335 -365 578 -706 649 -131 28 -307
21 -433 -16 -384 -113 -651 -448 -669 -838 -2 -38 -5 -68 -8 -68 -13 0 -90 89
-122 140 -234 373 -62 852 359 1001 72 26 269 37 337 20 36 -9 58 -8 113 6 47
11 116 16 220 17 84 1 153 3 153 6 0 9 -110 85 -177 122 -72 40 -182 79 -271
97 -73 15 -274 12 -351 -4z m290 -1230 c64 -42 99 -132 79 -204 -50 -187 -317
-189 -365 -3 -24 92 13 174 98 217 64 33 130 29 188 -10z"/>
                            </g>
                        </svg>
                    </div>
                    <h1 className="auth-title">Spiria</h1>
                    <p className="auth-subtitle">
                        {step === "email" ? "Войдите в свой аккаунт" : "Подтвердите вход"}
                    </p>
                </div>

                <div className="auth-card" data-step={step} data-auth-method={authMethod}>
                    {errorMessage && (
                        <ErrorMessage 
                            message={errorMessage} 
                            onDismiss={() => setErrorMessage("")}
                        />
                    )}
                    
                    {step === "code" && (
                        <InfoMessage 
                            message={`Код отправлен на ${email}`}
                        />
                    )}
                    
                    {step === "email" && (
                        <div className="auth-method-tabs">
                            <button
                                type="button"
                                className={`auth-method-tab ${authMethod === "email" ? "active" : ""}`}
                                onClick={() => {
                                    setAuthMethod("email");
                                    setErrorMessage("");
                                }}
                            >
                                Email
                            </button>
                            <button
                                type="button"
                                className={`auth-method-tab ${authMethod === "phone" ? "active" : ""}`}
                                onClick={() => {
                                    setAuthMethod("phone");
                                    setErrorMessage("");
                                }}
                            >
                                Телефон
                            </button>
                        </div>
                    )}
                    
                    {step === "email" ? (
                        <>
                            <div className="auth-method-panels">
                                <div
                                    className="auth-method-panel auth-method-panel--email"
                                    data-active={authMethod === "email" ? "true" : "false"}
                                    aria-hidden={authMethod !== "email"}
                                    inert={authMethod !== "email" ? true : undefined}
                                >
                                    <Form callback={handleSendCode}>
                                        <div className="auth-form-content">
                                            <EmailInput
                                                name="email"
                                                label="Электронная почта"
                                                example="mail@example.com"
                                                required={true}
                                                onchange={(e) => {
                                                    setEmail(e.target.value);
                                                    if (errorMessage) setErrorMessage("");
                                                }}
                                                initial={email}
                                            />
                                            <SubmitButton
                                                value={isSendingCode ? "Отправка..." : "Получить код"}
                                                disabled={isSendingCode}
                                            />
                                        </div>
                                    </Form>
                                    <p className="auth-hint auth-hint--under-email">
                                        На вашу почту будет отправлен код подтверждения
                                    </p>
                                </div>
                                <div
                                    className="auth-method-panel auth-method-panel--phone"
                                    data-active={authMethod === "phone" ? "true" : "false"}
                                    aria-hidden={authMethod !== "phone"}
                                    inert={authMethod !== "phone" ? true : undefined}
                                >
                                    <Form callback={handleSendSmsCode}>
                                        <div className="auth-form-content">
                                            <TextInput
                                                name="phone"
                                                label="Номер телефона"
                                                type="tel"
                                                example="+79991234567"
                                                required={true}
                                                onchange={(e) => {
                                                    setPhoneNational(parsePhoneInputToNationalDigits(e.target.value));
                                                    if (errorMessage) setErrorMessage("");
                                                }}
                                                initial={formatNationalAsE164Display(phoneNational)}
                                            />
                                            <SubmitButton
                                                value={isSendingSms ? "Отправка..." : "Получить код"}
                                                disabled={isSendingSms}
                                            />
                                        </div>
                                    </Form>
                                    <p className="auth-hint auth-hint--under-phone">
                                        Мы отправим SMS с инструкцией с указанного номера
                                    </p>
                                </div>
                            </div>

                            <div className="auth-oauth-section">
                                <p className="auth-oauth-label">Войти с помощью</p>
                                <div className="auth-oauth-buttons-small">
                                    <button
                                        type="button"
                                        className="auth-oauth-btn-small auth-oauth-yandex"
                                        onClick={() => handleOAuthAuthorize('yandex')}
                                        disabled={oauthBusy}
                                        title="Войти через Yandex"
                                    >
                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.04 12c0-5.523 4.476-10 10-10 5.522 0 10 4.477 10 10s-4.478 10-10 10c-5.524 0-10-4.477-10-10z" fill="#FC3F1D"/>
                                            <path d="M13.32 7.666h-.924c-1.694 0-2.585.858-2.585 2.123 0 1.43.616 2.1 1.881 2.959l1.045.704-3.003 4.487H7.49l2.695-4.014c-1.55-1.111-2.42-2.19-2.42-4.015 0-2.288 1.595-3.85 4.62-3.85h3.003v11.868H13.32V7.666z" fill="#fff"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        className="auth-oauth-btn-small auth-oauth-mail"
                                        onClick={() => handleOAuthAuthorize('mail')}
                                        disabled={oauthBusy}
                                        title="Войти через Mail.ru"
                                    >
                                        <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z" fill="#005FF9"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M10 23.9603C10 16.2625 16.2802 10 23.9998 10C31.7194 10 37.9997 16.2625 37.9997 23.9603C37.9997 25.0508 37.9087 25.9336 37.7048 26.8197L37.7018 26.8344C37.7013 26.837 37.6001 27.2455 37.5442 27.4237C37.1982 28.5272 36.5718 29.4463 35.7325 30.0815C34.9186 30.6977 33.9094 31.0371 32.891 31.0371C32.7652 31.0371 32.6385 31.032 32.5144 31.0221C31.0682 30.9063 29.7966 30.1474 29.0168 28.9373C27.6757 30.282 25.8964 31.0218 23.9998 31.0218C20.0951 31.0218 16.9184 27.854 16.9184 23.9603C16.9184 20.0666 20.0951 16.8989 23.9998 16.8989C27.9045 16.8989 31.0812 20.0666 31.0812 23.9603V26.2366C31.087 27.5648 31.9808 28.1077 32.7444 28.1691C33.5041 28.2276 34.5152 27.7863 34.8674 26.3461C35.0411 25.5562 35.1294 24.7533 35.1294 23.9603C35.1294 17.8407 30.1367 12.8621 23.9998 12.8621C17.8629 12.8621 12.8703 17.8407 12.8703 23.9603C12.8703 30.0798 17.8629 35.0585 23.9998 35.0585C26.136 35.0585 28.2136 34.4501 30.0079 33.2991L30.0401 33.2784L31.9261 35.465L31.8856 35.4926C29.5549 37.0811 26.828 37.9207 23.9998 37.9207C16.2802 37.9207 10 31.6581 10 23.9603ZM23.9998 28.1596C26.3218 28.1596 28.2109 26.2758 28.2109 23.9603C28.2109 21.6448 26.3218 19.7612 23.9998 19.7612C21.6777 19.7612 19.7887 21.6448 19.7887 23.9603C19.7887 26.2758 21.6777 28.1596 23.9998 28.1596Z" fill="#FF9E00"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        className="auth-oauth-btn-small auth-oauth-telegram"
                                        onClick={() => handleOAuthAuthorize('telegram')}
                                        disabled={oauthBusy}
                                        title="Войти через Telegram"
                                    >
                                        <svg width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path fill="#26A5E4" d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        className="auth-oauth-btn-small auth-oauth-vk"
                                        onClick={() => handleOAuthAuthorize('vk')}
                                        disabled={oauthBusy}
                                        title="Войти через VK ID"
                                    >
                                        <svg width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path fill="#0077FF" d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1.01-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.813 4.03 8.57 4.03 8.096c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.677.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.254-1.406 2.151-3.574 2.151-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .643.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.475-.085.72-.576.72z"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        className="auth-oauth-btn-small auth-oauth-max"
                                        onClick={handleMaxLogin}
                                        disabled={oauthBusy}
                                        title="Войти через MAX"
                                    >
                                        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <rect width="40" height="40" rx="12" fill="#4C4CFF"/>
                                            <text x="20" y="25" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="system-ui,sans-serif">MAX</text>
                                        </svg>
                                    </button>
                                </div>
                                {isMaxWaiting ? (
                                    <p className="auth-hint">Откройте MAX и нажмите Start. Ждём подтверждение…</p>
                                ) : null}
                            </div>
                        </>
                    ) : (
                        <Form callback={handleVerifyCode}>
                            <div className="auth-form-content">
                                <label className="auth-code-label">
                                    Введите код из письма
                                </label>
                                <TextInput 
                                    name="code" 
                                    label="Код подтверждения"
                                    type="text"
                                    pattern="\w{6}"
                                    required={true} 
                                    onchange={(e) => {
                                        setCode(e.target.value);
                                        if (errorMessage) setErrorMessage("");
                                    }}
                                    initial={code}
                                    className="form-code-center"
                                />
                                <SubmitButton 
                                    value={isVerifying ? "Вход..." : "Войти"} 
                                    disabled={isVerifying} 
                                />
                                
                                <div className="auth-resend-section">
                                    <p className="auth-resend-hint">
                                        Код не пришёл? Сначала проверьте папку «Спам», затем запросите новый
                                    </p>
                                    <button
                                        type="button"
                                        className={`auth-resend-btn ${canResend ? 'active' : 'disabled'}`}
                                        onClick={handleResendCode}
                                        disabled={!canResend || isSendingCode}
                                    >
                                        {isSendingCode ? (
                                            "Отправка..."
                                        ) : canResend ? (
                                            "Отправить код повторно"
                                        ) : (
                                            `Отправить повторно через ${resendTimer} сек.`
                                        )}
                                    </button>
                                </div>
                                
                                <button 
                                    type="button" 
                                    className="auth-back-btn"
                                    onClick={handleBackToEmail}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m15 18-6-6 6-6"></path>
                                    </svg>
                                    Изменить адрес почты
                                </button>
                            </div>
                        </Form>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}

export default LoginEmail;
